import type {
  ScorecardItem,
  ScorecardTemplateFormValues,
} from '@kula-ai/components';
import { getScorecardInitialValues } from '@/utils/schedule-interview-helpers';

type Values = Partial<ScorecardTemplateFormValues> | undefined;

/**
 * The fields a user can actually change, in a stable order. `id` is excluded:
 * templates key questions by index and payloads by row id, so comparing it
 * would report every seeded scorecard as edited.
 */
function projectQuestion(item: ScorecardItem) {
  return [
    item.question?.trim() ?? '',
    item.type ?? 'open_question',
    item.description?.trim() ?? '',
    Boolean(item.mandatory),
    (item.options ?? []).map((option) => option.optionText?.trim() ?? ''),
  ];
}

function project(values: Values) {
  return JSON.stringify([
    (values?.scoreCards ?? []).map(projectQuestion),
    values?.feedback_description?.trim() ?? '',
    Boolean(values?.is_feedback_mandatory),
    values?.rating_description?.trim() ?? '',
  ]);
}

/**
 * Whether the builder's contents have diverged from the selected template.
 * A content diff, not a dirty flag — undoing an edit restores the template
 * name, matching the email composer's behaviour.
 *
 * Both sides are compared through the same projection because the two shapes
 * reach the form via different mappers (`templateToFormValues` vs
 * `payloadToFormValues`) and differ in ids and optional-vs-empty fields.
 */
export function isScorecardEdited(current: Values, template: Values): boolean {
  // No template selected, or one that ships no content, has nothing to diverge
  // from — the picker should keep showing whatever is selected.
  if (!template || project(template) === project(undefined)) return false;
  return project(current) !== project(template);
}

/**
 * Map a scorecard template *picker option* (the shape both legacy surfaces
 * build their options in) into builder form values, so it can be diffed
 * against what the user currently has.
 */
export function templateOptionToFormValues(
  option: Record<string, unknown> | null | undefined
): ScorecardTemplateFormValues | undefined {
  if (!option) return undefined;
  return {
    scoreCards: getScorecardInitialValues(
      (option.questions as unknown[]) ?? []
    ),
    feedback_description: (option.feedback_description as string) ?? '',
    is_feedback_mandatory: Boolean(option.is_feedback_mandatory),
    rating_description: (option.rating_description as string) ?? '',
  };
}

/** Just the questions, for recognising which template a scorecard came from. */
function projectQuestions(values: Values) {
  return JSON.stringify((values?.scoreCards ?? []).map(projectQuestion));
}

/**
 * The first option whose *questions* match the current ones. Used to recognise
 * a saved scorecard that was never edited, so the picker can name its template
 * instead of falling back to "Custom".
 *
 * Questions only, deliberately: a copy records its own feedback flag and
 * descriptions, which routinely differ from the template's even though the
 * recruiter changed nothing. Divergence *after* a template is selected is
 * still judged on the full content by {@link isScorecardEdited}.
 */
export function findMatchingTemplateOption<T extends object>(
  options: T[],
  current: Values
): T | undefined {
  const target = projectQuestions(current);
  if (target === projectQuestions(undefined)) return undefined;
  return options.find((option) => {
    const template = templateOptionToFormValues(
      option as Record<string, unknown>
    );
    return !!template && projectQuestions(template) === target;
  });
}
